import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { Session } from '../types/sessions';
import { sessionsApi } from '../services/sessions/sessionsApi';
import { useAuth } from './AuthContext';

interface SessionContextValue {
  /** The session marked status='active' at the data layer (writes go here). */
  activeSession: Session | null;
  /**
   * The session the current user is "viewing" in the UI. Defaults to active.
   * Changing it filters list pages but does not change where writes land
   * (writes always target activeSession). See plan §9.2.
   */
  viewSession: Session | null;
  setViewSession: (session: Session | null) => void;
  /** Reload active + reset view to active. */
  refresh: () => Promise<void>;
  loading: boolean;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

interface SessionContextProviderProps {
  children: ReactNode;
}

export const SessionContextProvider: React.FC<SessionContextProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [viewSession, setViewSessionState] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!user) {
      setActiveSession(null);
      setViewSessionState(null);
      return;
    }
    setLoading(true);
    try {
      const active = await sessionsApi.getActive();
      setActiveSession(active);
      // Reset view to active whenever active changes.
      setViewSessionState(active);
    } catch {
      // Silent — empty state UI handles no-active scenario.
      setActiveSession(null);
      setViewSessionState(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const setViewSession = useCallback((session: Session | null) => {
    setViewSessionState(session ?? activeSession);
  }, [activeSession]);

  const value = useMemo<SessionContextValue>(() => ({
    activeSession,
    viewSession,
    setViewSession,
    refresh: load,
    loading
  }), [activeSession, viewSession, setViewSession, load, loading]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSessionContext = (): SessionContextValue => {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSessionContext must be used within a SessionContextProvider');
  return ctx;
};
